import React, { MouseEventHandler } from 'react';
import { useAppDispatch } from 'flow/hooks/useRedux';

import { showImagePreview } from 'flow/actions/chat-actions';
import { ContactFull } from 'flow/types/Contact';
import { Statuses } from 'flow/types/Extension';
import { TypedMap } from 'flow/types/TypedMap';
import {
    extractInitials,
    getBgColorFromContact_TW,
    getBgColorFromKey_TW,
    getFlowExtensionContactAvatar,
} from 'flow/utils';

import FALLBACK_SVG from '../../../images/missing-image-person-transparent.svg';
import UserAvatar from './UserAvatar';
import AiUserAvatar from 'flow/components/ui-components/AiUserAvatar';
import { AvatarSize } from './ChatAvatar';

interface Props {
    contact?: TypedMap<ContactFull>;
    status?: Statuses;
    size?: AvatarSize;
    lazyLoad?: boolean;
    canShowImagePreview?: boolean;
    fallBackBackgroundColorClass?: string;
    shouldUseDefaultIfNoFirstNameAndNoLastName?: boolean;
    ignoreAvatarResource?: boolean;
    contactType?: string;
}

const ContactAvatar = ({
    contact,
    status,
    size,
    lazyLoad,
    canShowImagePreview,
    fallBackBackgroundColorClass = 'bg-gray-200',
    shouldUseDefaultIfNoFirstNameAndNoLastName = false,
    ignoreAvatarResource,
    contactType = 'user',
}: Props) => {
    const dispatch = useAppDispatch();

    const handleShowImage: MouseEventHandler = (event) => {
        if (canShowImagePreview && !ignoreAvatarResource) {
            if (event && event.stopPropagation) {
                event.stopPropagation();
            }
            const src = getFlowExtensionContactAvatar(contact, FALLBACK_SVG);
            dispatch(showImagePreview(src));
        }
    };

    const contactKey = contact?.get('key');
    let backgroundColorClass = getBgColorFromKey_TW(contactKey);

    if (!contactKey) {
        backgroundColorClass = getBgColorFromContact_TW(contact);
    }

    let AvatarSrc;
    let initials = extractInitials(contact, '');
    // What UserAvatar shows until the image does, e.g. while a lazily loaded row is still
    // out of view.
    let placeholder: React.ReactNode = null;

    if (contact?.get('avatarResource') && !ignoreAvatarResource) {
        AvatarSrc = getFlowExtensionContactAvatar(contact, FALLBACK_SVG);
        initials = null;
        backgroundColorClass = '';
    } else if (
        !contact ||
        contact.isEmpty() ||
        // No image and no initials: the person icon, never an empty circle (AC-2160). Not for
        // an AI coworker, whose avatar draws its own shape from the flavour colour.
        (!initials && contactType !== 'ai_coworker') ||
        (shouldUseDefaultIfNoFirstNameAndNoLastName &&
            !contact.get('firstName') &&
            !contact.get('lastName'))
    ) {
        AvatarSrc = getFlowExtensionContactAvatar(contact, FALLBACK_SVG);
        initials = null;
        // The same person icon, so the circle is never empty while loading either.
        placeholder = (
            <img
                className="size-full rounded-full object-cover"
                src={FALLBACK_SVG}
                alt=""
            />
        );
        backgroundColorClass = fallBackBackgroundColorClass;
    } else {
        AvatarSrc = null;
    }
    if (contactType === 'ai_coworker') {
        return (
            <AiUserAvatar
                size={'toolbar'}
                className={backgroundColorClass}
                count={null}
                status={status}
            >
                {initials}
            </AiUserAvatar>
        );
    }

    return (
        <UserAvatar
            size={size}
            onClick={canShowImagePreview && handleShowImage}
            className={backgroundColorClass}
            src={AvatarSrc}
            count={null}
            status={status}
            lazyLoad={lazyLoad}
        >
            {placeholder ?? initials}
        </UserAvatar>
    );
};

export default ContactAvatar;
